import { toast } from 'sonner';
import { SESSION_EXPIRED_MESSAGE, type ApiError } from './errors';
import { isSessionEndDeferred } from './session';

/**
 * Display the actionable error message. Request identifiers stay on the API
 * error for diagnostics rather than appearing in user-facing toasts.
 *
 * NOT-715 — every lapsed-session error shares one toast. A lapsed session is
 * discovered once per in-flight request, and each discovery arrives here
 * separately: the query cache raises one per failed read, the mutation hook one
 * per failed write. Sonner
 * mints a fresh id per call, so those stacked up as identical "Session expired"
 * alerts. A stable id makes Sonner update the existing toast instead of adding
 * another, so the user sees exactly one however many requests were in the air.
 * `client.ts` single-flights the logout itself; this is the second half, for
 * errors raised before that guard closed.
 *
 * Scoped to the interceptor's canonical session-expiry error deliberately.
 * Other 401s (for example, bad credentials from public auth) and ordinary
 * failures must keep stacking instead of hiding separate problems.
 */
const SESSION_EXPIRED_TOAST_ID = 'session-expired';

export function toastApiError(error: ApiError, message: string = error.message): void {
  if (error.status === 401 && error.message === SESSION_EXPIRED_MESSAGE) {
    // While the sign-out waits for a call to end, the toast under this id
    // says so; "please log in again" over it would read as already signed out.
    if (isSessionEndDeferred()) return;
    toast.error(message, {
      id: SESSION_EXPIRED_TOAST_ID,
    });
    return;
  }

  toast.error(message);
}
